import { useSectionPermission, type PermissionSection } from '@artic/api-client';
import { Alert, AlertDescription } from '@artic/ui/alert';
import { cn } from '@artic/ui/lib/utils';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/** "You have read-only access to this section, so changes are turned off." */
export function ReadOnlyBanner({ className }: { className?: string | undefined }) {
  const { t } = useTranslation('permissions');
  return (
    <Alert className={cn('w-auto', className)}>
      <AlertDescription>{t('readOnlyNotice')}</AlertDescription>
    </Alert>
  );
}

/**
 * The banner for a factory-scoped page: shown only when the user can write
 * `section` in some factory but not in `factoryId`. The "read-only
 * everywhere" case is the root layout's `RouteReadOnlyBanner`, so a page
 * never shows two banners.
 */
export function FactoryReadOnlyBanner({
  section,
  factoryId,
  className,
}: {
  section: PermissionSection;
  factoryId: string | null | undefined;
  className?: string | undefined;
}) {
  const show = useFactoryReadOnlyBanner(section, factoryId);
  return show ? <ReadOnlyBanner className={className} /> : null;
}

/** Whether `FactoryReadOnlyBanner` would show — for pages that place it themselves. */
export function useFactoryReadOnlyBanner(
  section: PermissionSection,
  factoryId: string | null | undefined,
): boolean {
  const inFactory = useSectionPermission(section, factoryId ? { factoryId } : undefined);
  const anywhere = useSectionPermission(section);
  return anywhere.canWrite && !inFactory.canWrite;
}

/**
 * Permissions v1: renders `children` untouched for a user with write on
 * `section`; for a read-only user, shows the banner and wraps them in a
 * disabled fieldset, which turns off every native control and every Radix
 * control (they render real buttons) inside it.
 *
 * What a fieldset does NOT cover:
 * - Portaled content (Radix dialogs, sheets, popovers, select menus) renders
 *   outside it. It stays safe only while every trigger sits inside.
 * - AG Grid editing, fill handles and context menus: pass a `locked` /
 *   `readOnly` input to the grid instead.
 * - Handlers fired outside native controls (drag-and-drop, keyboard
 *   shortcuts, blur autosaves on non-form elements).
 * Hide a write affordance that lives outside the wrapped content (e.g. a
 * floating release bar) with `useSectionPermission(section).canWrite`.
 */
export function ReadOnlySection({
  section,
  factoryId,
  banner = true,
  bannerClassName,
  children,
}: {
  section: PermissionSection;
  /** Check this factory's level instead of the partner-wide maximum. */
  factoryId?: string | null;
  /**
   * Show the read-only banner above the content (default true). With a
   * `factoryId` prop (even null) it follows `FactoryReadOnlyBanner`: shown only when the user
   * can write in another factory (the root layout covers read-everywhere).
   */
  banner?: boolean;
  bannerClassName?: string;
  children: ReactNode;
}) {
  const { canWrite } = useSectionPermission(section, factoryId ? { factoryId } : undefined);
  const { canWrite: canWriteAnywhere } = useSectionPermission(section);
  if (canWrite) return <>{children}</>;
  // Factory-scoped (a `factoryId` prop, even a not-yet-chosen null): the root
  // layout already covers "read-only everywhere", so only the narrower case
  // gets a banner here.
  const showBanner = banner && (factoryId === undefined || canWriteAnywhere);
  return (
    <fieldset disabled className="contents" data-testid="read-only-section">
      {showBanner && <ReadOnlyBanner className={bannerClassName} />}
      {children}
    </fieldset>
  );
}
